/**
 * The company portal's browser sign-in, as pure helpers (design 20-identity-design.md §4.1,
 * §8.1): the `state` that says which provider a redirect came back from, PKCE, reading the
 * redirect, and the hints a client app's link-out carries (40-client-apps-design.md §1.7).
 *
 * The portal is one static page with one redirect URI per environment for every provider
 * (`https://<portal host>/`). What tells a returning code apart is the `state`, prefixed by
 * provider (`ms.<random>`); the PKCE verifier, the nonce and what the round trip was *for*
 * (sign in, link an organisation, connect a data scope) wait in sessionStorage keyed by
 * that state. The API redeems the code server-side as a confidential client.
 *
 * The request and response shapes of the provider routes are the providers' own
 * (microsoft.ts, google.ts, emailAuth.ts): the portal compiles against what the API
 * answers, not a copy of it.
 */
import type { ClientKind } from './clientApps'
import { isClientKind } from './clientApps'
import type { Platform } from './identity'

/** Providers that come back to the portal through a browser redirect. */
export type RedirectProvider = 'lark' | 'microsoft' | 'google'

const STATE_PREFIX: Record<RedirectProvider, string> = { lark: 'lark', microsoft: 'ms', google: 'google' }
const PROVIDER_OF_PREFIX: Record<string, RedirectProvider> = { lark: 'lark', ms: 'microsoft', google: 'google' }

/** base64url of `byteLength` random bytes: verifiers, nonces and state. */
export function randomUrlSafe(byteLength = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(byteLength)))
}

/** `<prefix>.<random>`: the provider is readable from the state alone when the browser returns. */
export function makeOAuthState(provider: RedirectProvider, random: string = randomUrlSafe(24)): string {
  return `${STATE_PREFIX[provider]}.${random}`
}

/**
 * Which provider a returning `state` belongs to, or null for one this portal never makes.
 *
 * A state without a dot is the Lark sign-in of the release before this one (a bare UUID):
 * a tab that left for Lark's screen before the deploy comes back to a portal that must
 * still finish it.
 */
export function providerOfOAuthState(state: string | null | undefined): RedirectProvider | null {
  if (!state || state.length > 512) return null
  const dot = state.indexOf('.')
  if (dot === -1) return /^[0-9a-f-]{36}$/i.test(state) ? 'lark' : null
  return PROVIDER_OF_PREFIX[state.slice(0, dot)] ?? null
}

/** A PKCE verifier (RFC 7636: 43–128 characters of the unreserved set). */
export function makeCodeVerifier(): string {
  return randomUrlSafe(48) // 64 characters
}

/** The S256 challenge for a verifier: base64url(SHA-256(verifier)), no padding. */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(new Uint8Array(digest))
}

function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** What a provider's redirect to the portal carried. */
export type OAuthCallback =
  | { kind: 'code'; code: string; state: string | null }
  | { kind: 'error'; error: string; description: string | null; state: string | null }
  /** Microsoft's admin-consent endpoint answers with these instead of a code. */
  | { kind: 'adminConsent'; granted: boolean; tenant: string | null; state: string | null }

/** The parameters the portal strips from the address bar once read. */
export const OAUTH_CALLBACK_PARAMS = ['code', 'state', 'error', 'error_description', 'error_uri', 'admin_consent', 'tenant', 'session_state', 'scope', 'authuser', 'hd', 'prompt', 'iss'] as const

/**
 * Read a redirect's query string. A code wins over everything; an error needs a state
 * (a bare `?error=` from anywhere else is not ours to explain); Microsoft's admin consent
 * is recognised by its `admin_consent` flag.
 */
export function readOAuthCallback(search: URLSearchParams): OAuthCallback | null {
  const state = search.get('state')
  const code = search.get('code')
  if (code) return { kind: 'code', code, state }
  const adminConsent = search.get('admin_consent')
  if (adminConsent !== null && state) {
    return { kind: 'adminConsent', granted: adminConsent.toLowerCase() === 'true', tenant: search.get('tenant'), state }
  }
  const error = search.get('error')
  if (error && state) return { kind: 'error', error, description: search.get('error_description'), state }
  return null
}

/** Who a client app's link-out asked the chooser to sign in (40 §1.7). */
export interface SignInHints {
  provider?: 'microsoft' | 'google' | 'email'
  /** An address to pre-fill: `login_hint` for Microsoft and Google, the email field otherwise. */
  hint?: string
  from?: ClientKind
}

// Generous but bounded: an address is at most 254 characters; nothing here is trusted
// beyond pre-filling a field or a login_hint the provider checks itself.
const HINT = /^[^\s@<>"]{1,64}@[^\s@<>"]{1,189}$/

/**
 * The hints in `#/billing?from=excel&hint=a@b.com&provider=microsoft`. Each is dropped
 * unless well-formed; no credential ever travels this way, so nothing here is a secret.
 */
export function parseSignInHints(query: Record<string, unknown>): SignInHints {
  const one = (value: unknown): string | undefined => (Array.isArray(value) ? one(value[0]) : typeof value === 'string' ? value : undefined)
  const hints: SignInHints = {}
  const provider = one(query.provider)
  if (provider === 'microsoft' || provider === 'google' || provider === 'email') hints.provider = provider
  const hint = one(query.hint)?.trim()
  if (hint && HINT.test(hint)) hints.hint = hint
  const from = one(query.from)
  if (isClientKind(from)) hints.from = from
  return hints
}

/**
 * The claims of one of our JWTs, read without verifying it — for a browser deciding how
 * to refresh and what to show, never for a decision the server does not make again.
 * `plat` absent means Lark (every token issued before other platforms existed).
 */
export function readJwtClaims(token: string | null | undefined): { platform: Platform; exp: number | null; tenantKey: string | null; userId: string | null } | null {
  if (!token) return null
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const binary = atob(part.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (part.length % 4)) % 4))
    const claims = JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, (ch) => ch.charCodeAt(0)))) as Record<string, unknown>
    const plat = claims.plat ?? 'lark'
    if (plat !== 'lark' && plat !== 'microsoft' && plat !== 'google' && plat !== 'email') return null
    const platform: Platform = plat
    return {
      platform,
      exp: typeof claims.exp === 'number' ? claims.exp * 1000 : null,
      tenantKey: typeof claims.tenantKey === 'string' ? claims.tenantKey : null,
      userId: typeof claims.userId === 'string' ? claims.userId : null,
    }
  } catch {
    return null
  }
}
